import './index.scss';

import { FormEvent, useEffect, useState } from 'react';
import Modal from 'react-modal';

import { useAuth } from '@auth/AuthProvider';
import ContextMenu from '@components/ContextMenu';
import LoadingIndicator from '@components/LoadingIndicator';
import SongSearchController, { SongSearchResultsResponse } from '@data/controller/SongSearchController';
import { SongAttributes, StreamingPlatform } from '@data/interfaces/song.interface';

import appleMusicIcon from 'url:@assets/images/services/apple-music.png';
import spotifyIcon from 'url:@assets/images/services/spotify.png';
import youtubeMusicIcon from 'url:@assets/images/services/youtube-music.jpg';
import tidalIcon from 'url:@assets/images/services/tidal.jpg';
import closeIcon from 'url:@icon/close.svg';
import backIcon from 'url:@icon/chevron.left.plain.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';
import optionsIcon from 'url:@assets/icons/options-icon.svg';
import linkArrowIcon from 'url:@assets/icons/linkArrow.svg';
import songPlaceholder from 'url:@assets/images/SongPlaceholder.png';
import SuccessView from '@components/SuccessView';
import ErrorView from '@components/ErrorView';

type ModalStep = 'search' | 'results' | 'matching' | 'matches' | 'confirmation';

type MatchRow = {
  service: StreamingPlatform;
  title: string;
  artist: string;
  artworkURL: string | null;
  matchedURL: string | null;
  externalPlatformID: string | null;
};

type MatchResultsResponse = {
  matchResults: Partial<Record<StreamingPlatform, SongAttributes>>;
};

export type SongSearchModalSelection = {
  selectedSong: SongAttributes;
};

export type SongSearchModalConfirmationResult = {
  success: boolean;
  title: string;
  message: string;
}

const STREAMING_PLATFORMS: StreamingPlatform[] = [
  'Apple Music',
  'Spotify',
  'YouTube Music',
  'Tidal'
];

export default function SongSearchModal(props: {
  isOpen: boolean;
  id: string;
  close: () => void;
  initialQuery?: string;
  onConfirm: (selection: SongSearchModalSelection) => Promise<SongSearchModalConfirmationResult> | void;
}) {
  const auth = useAuth();

  const [step, setStep] = useState<ModalStep>('search');
  const [query, setQuery] = useState(props.initialQuery || '');
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isMatching, setIsMatching] = useState(false);
  const [results, setResults] = useState<SongSearchResultsResponse | null>(null);
  const [selectedSong, setSelectedSong] = useState<SongAttributes | null>(null);
  const [matchRows, setMatchRows] = useState<MatchRow[]>([]);
  const [confirmationResult, setConfirmationResult] = useState<SongSearchModalConfirmationResult | null>(null);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    setStep('search');
    setQuery(props.initialQuery || '');
    setError(null);
    setIsSearching(false);
    setIsMatching(false);
    setResults(null);
    setSelectedSong(null);
    setMatchRows([]);
    setConfirmationResult(null);
  }, [props.initialQuery, props.isOpen]);

  const submitSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setError('Enter a song title or artist to start searching.');
      return;
    }

    setError(null);
    setResults(null);
    setStep('results');
    setIsSearching(true);

    const response = await SongSearchController.searchSongs(trimmedQuery, auth);
    setIsSearching(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.body) {
      const body = response.body;

      if (body.results.length === 0) {
        setError('No songs matched that search. Try a different title, artist, or spelling.');
        return;
      }

      setResults(body);
    } else {
      setError('An unexpected error occurred. Please try again.');
      return;
    }
  };

  const selectSong = async (song: SongAttributes, platform: StreamingPlatform) => {
    setSelectedSong(song);
    setError(null);
    setMatchRows([]);
    setStep('matching');
    setIsMatching(true);

    const response = await SongSearchController.fetchMatchResults(song.isrc || '', platform, auth);
    setIsMatching(false);

    if (response.error) {
      setError(response.error);
      setStep('results');
      return;
    }

    const rows = buildMatchRows(response.body, song, platform);
    setMatchRows(rows);
    setStep('matches');

    if (rows.length === 0) {
      setError('No streaming match results were found.');
    }
  };

  const confirmMatches = () => {
    if (!selectedSong || !props.onConfirm || matchRows.length === 0) {
      return;
    }

    let merged = selectedSong;
    matchRows.forEach((row) => {
      if (merged.streamingLinks.find((link) => link.platform === row.service)) {
        return;
      }

      if (row.matchedURL && row.externalPlatformID) {
        merged.streamingLinks.push({
          platform: row.service,
          url: row.matchedURL
        });

        merged.externalIDs.push({
          platform: row.service,
          id: row.externalPlatformID
        });
        return;
      }

      const searchURL = getSearchURLForService(selectedSong, row.service) || undefined
      if (searchURL) {
        merged.streamingLinks.push({
          platform: row.service,
          url: searchURL
        });
      }
    });

    const result = props.onConfirm({
      selectedSong: merged
    });

    if (result instanceof Promise) {
      setStep('confirmation');

      result.then((confirmationResult) => {
        setConfirmationResult(confirmationResult);
      });
    } else {
      props.close();
    }
  };

  return <Modal
    isOpen={props.isOpen}
    id={props.id}
    closeTimeoutMS={200}
    className='modal song-search-modal-shell'
  >
    <div className='song-search-modal'>
      <div className='song-search-modal-viewport'>
        <div className='song-search-modal-step'>
          {step === 'search' ? (
            <SearchStep
              query={query}
              disabled={isSearching}
              onClose={props.close}
              onQueryChange={(value) => {
                setQuery(value);
                setError(null);
              }}
              onSubmit={(e) => {
                submitSearch(e);
              }}
            />
          ) : null}

          {step === 'results' ? (
            <ResultsStep
              query={query}
              loading={isSearching}
              response={results}
              onBack={() => {
                setStep('search');
                setError(null);
              }}
              onSelectSong={(song, platform) => {
                void selectSong(song, platform);
              }}
            />
          ) : null}

          {step === 'matching' ? (
            <MatchingStep
              song={selectedSong}
              loading={isMatching}
              onBack={() => {
                if (isMatching) return;

                setStep('results');
                setError(null);
              }}
            />
          ) : null}

          {step === 'matches' ? (
            <MatchesStep
              song={selectedSong}
              rows={matchRows}
              disableBack={isMatching}
              removeMatchForService={(service) => {
                setMatchRows((matchRows) =>
                  matchRows.map((row) => {
                    if (row.service === service) {
                      return {
                        ...row,
                        title: 'Marked as Incorrect',
                        artist: 'Search link used for this platform instead.',
                        artworkURL: null,
                        matchedURL: null
                      };
                    }

                    return row;
                  })
                );
              }}
              onBack={() => {
                setStep('results');
                setError(null);
              }}
              onConfirm={confirmMatches}
            />
          ) : null}

          {step === 'confirmation' ?
            <ConfirmationStep
              result={confirmationResult}
              close={props.close} /> : null}
        </div>
      </div>
    </div>
  </Modal>;
}

function SearchStep(props: {
  query: string;
  disabled: boolean;
  onClose: () => void;
  onQueryChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return <>
    <StepHeader
      title='Song Search'
      action='close'
      disabled={props.disabled}
      onAction={props.onClose}
    />

    <form className='song-search-modal-search-bar' onSubmit={props.onSubmit}>
      <input
        id="query"
        type='text'
        value={props.query}
        disabled={props.disabled}
        placeholder='Search by song title or artist'
        onChange={(event) => {
          props.onQueryChange(event.target.value);
        }}
      />

      <button type='submit' disabled={props.disabled}>Search</button>
    </form>
  </>;
}

function ResultsStep(props: {
  query: string;
  loading: boolean;
  response: SongSearchResultsResponse | null;
  onBack: () => void;
  onSelectSong: (song: SongAttributes, platform: StreamingPlatform) => void;
}) {
  const response = props.response;

  return <>
    <StepHeader
      title='Search Results'
      action='back'
      disabled={props.loading}
      onAction={props.onBack}
    />

    {!props.loading && <div className='song-search-modal-query-label'>Showing results for "{props.query}"</div>}

    {props.loading ? <div className='song-search-modal-loading-state'>
      <LoadingIndicator fullScreen={false} />
    </div> : null}

    {!props.loading && response && response.results.length > 0 ? <div className='song-search-modal-list'>
      {response.results.map((song, index) => {
        return <button
          key={`${song.isrc}-${index}`}
          type='button'
          className='song-search-modal-card song-search-modal-result-card card'
          onClick={() => {
            props.onSelectSong(song, response.platform);
          }}
        >
          <img
            className='song-search-modal-artwork'
            src={song.artworkURL || ''}
            alt={song.title}
          />

          <div className='song-search-modal-copy'>
            <h5>{song.title}</h5>
            <p>{song.artist.name}</p>
          </div>
        </button>;
      })}
    </div> : null}

    {!props.loading && <EmptyState message='No results.' />}
  </>;
}

function MatchingStep(props: {
  song: SongAttributes | null;
  loading: boolean;
  onBack: () => void;
}) {
  return <>
    <StepHeader
      title='Match Results'
      action='back'
      disabled={props.loading}
      onAction={props.onBack}
    />

    <div className='song-search-modal-loading-state'>
      <LoadingIndicator fullScreen={false} />
    </div>
  </>;
}

function MatchesStep(props: {
  song: SongAttributes | null;
  rows: MatchRow[];
  disableBack: boolean;
  removeMatchForService: (service: StreamingPlatform) => void;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const canConfirm = props.rows.length > 0;
  const [activeMenu, setActiveMenu] = useState<{
    service: StreamingPlatform;
    x: number;
    y: number;
  } | null>(null);

  return <>
    <StepHeader
      title='Match Results'
      action='back'
      disabled={props.disableBack}
      onAction={props.onBack}
      confirmAction={props.onConfirm}
    />

    {props.rows.length > 0 ? <div className='song-search-modal-list'>
      {props.rows.map((row) => {
        return <div key={row.service} className='song-search-modal-match-group'>
          <div className='song-search-modal-service-header'>
            {getServiceIcon(row.service) ? (
              <img
                className='song-search-modal-service-icon'
                src={getServiceIcon(row.service) || ''}
                alt=""
                aria-hidden="true"
              />
            ) : null}
            <h5>{formatServiceLabel(row.service)}</h5>
          </div>

          <div className='song-search-modal-card song-search-modal-match-card card'>
            <img
              className='song-search-modal-artwork'
              src={row.artworkURL || songPlaceholder}
              alt={row.title}
            />

            <div className='song-search-modal-copy'>
              <h5>{row.title}</h5>
              {row.artist ? <p>{row.artist}</p> : null}
            </div>

            <div className='song-search-modal-match-actions'>
              <button
                type='button'
                className='song-search-modal-options-button'
                onClick={(event) => {
                  const rect = event.currentTarget.getBoundingClientRect();
                  setActiveMenu({
                    service: row.service,
                    x: rect.left + rect.width + window.scrollX,
                    y: rect.top + window.scrollY
                  });
                }}
              >
                <img src={optionsIcon} alt="Match actions" />
              </button>
            </div>

            {activeMenu?.service === row.service ? <ContextMenu
              className='song-search-match-menu'
              x={activeMenu.x}
              y={activeMenu.y}
              onRequestClose={() => {
                setActiveMenu(null);
              }}
              sections={[{
                className: 'song-search-match-menu-actions',
                items: [
                  {
                    key: 'open-link',
                    className: 'song-search-match-menu-action',
                    href: row.matchedURL || getSearchURLForService(props.song!, row.service) || undefined,
                    disabled: false,
                    onClick: () => {
                      setActiveMenu(null);
                    },
                    content: <>
                      <img src={linkArrowIcon} alt="" aria-hidden="true" />
                      <span>Open Link</span>
                    </>
                  },
                  row.matchedURL ? {
                    key: 'incorrect-match',
                    className: 'song-search-match-menu-action danger',
                    disabled: false,
                    onClick: () => {
                      setActiveMenu(null);
                      props.removeMatchForService(row.service);
                    },
                    content: <>
                      <span>Incorrect Match</span>
                    </>
                  } : null
                ]
              }]}
            /> : null}
          </div>
        </div>;
      })}
    </div> : <EmptyState message='No match results were returned for this song.' />}
  </>;
}

function ConfirmationStep(props: {
  result: SongSearchModalConfirmationResult | null;
  close: () => void;
}) {
  const loading = props.result === null;

  return <>
    <StepHeader
      title=''
      action='close'
      disabled={loading === true}
      onAction={() => {
        props.close();
      }}
    />

    {loading === true ? <div className='song-search-modal-loading-state' style={{ marginBottom: '2rem'}}>
      <LoadingIndicator fullScreen={false} />
    </div> : null}

    {props.result && <div className='song-search-modal-confirmation'>
      {props.result.success ?
        <SuccessView
          title={props.result.title}
          description={props.result.message} /> :
        <ErrorView
          showButton={false}
          error={props.result.message} />}
    </div>}
  </>;
}

function EmptyState(props: {
  message: string;
}) {
  return <div className='song-search-modal-empty'>{props.message}</div>;
}

function StepHeader(props: {
  title: string;
  action: 'close' | 'back';
  disabled: boolean;
  onAction: () => void;
  confirmAction?: () => void;
}) {
  return <div className='song-search-modal-header'>
    <button
      type='button'
      className={`song-search-modal-header-control ${props.action}`}
      disabled={props.disabled}
      onClick={props.onAction}>
      {props.action === 'close' ? <img src={closeIcon} alt="Close icon" /> : <img src={backIcon} alt="Back icon" />}
    </button>

    <h3>{props.title}</h3>

    {props.confirmAction ? <button
      type='button'
      className='song-search-modal-header-control confirm'
      disabled={props.disabled}
      onClick={() => {
        props.confirmAction?.();
      }}>
      <img src={checkmarkIcon} alt="Confirm icon" />
    </button> : <div className='song-search-modal-header-spacer' />}
  </div>;
}

function buildMatchRows(
  response: MatchResultsResponse | undefined,
  selectedSong: SongAttributes,
  selectedSongPlatform: StreamingPlatform
): MatchRow[] {
  return STREAMING_PLATFORMS.map((service) => {
    let matchedSong = response?.matchResults[service];
    if (service === selectedSongPlatform) {
      matchedSong = selectedSong;
    }

    if (!matchedSong) {
      return {
        service,
        title: 'No Match Found',
        artist: 'Search link used for this platform instead.',
        artworkURL: null,
        matchedURL: null,
        externalPlatformID: null
      };
    }

    return {
      service,
      title: matchedSong.title,
      artist: matchedSong.artist.name,
      artworkURL: matchedSong.artworkURL,
      matchedURL: matchedSong.streamingLinks.find((link) => link.platform === service)?.url || null,
      externalPlatformID: matchedSong.externalIDs.find((id) => id.platform === service)?.id || null
    };
  });
}

function formatServiceLabel(service: StreamingPlatform): string {
  return service;
}

function getServiceIcon(service: StreamingPlatform): string | null {
  if (service === 'Apple Music') {
    return appleMusicIcon;
  }

  if (service === 'Spotify') {
    return spotifyIcon;
  }

  if (service === 'YouTube Music') {
    return youtubeMusicIcon;
  }

  if (service === 'Tidal') {
    return tidalIcon;
  }

  return null;
}

function getSearchURLForService(song: SongAttributes, service: StreamingPlatform): string | null {
  if (service === 'Apple Music') {
    return `https://music.apple.com/search?term=${encodeURIComponent(song.title + ' ' + song.artist.name)}&entity=song`;
  }

  if (service === 'Spotify') {
    return `https://open.spotify.com/search/${encodeURIComponent(song.title + ' ' + song.artist.name)}`;
  }

  if (service === 'YouTube Music') {
    return `https://music.youtube.com/search?q=${encodeURIComponent(song.title + ' ' + song.artist.name)}`;
  }

  if (service === 'Tidal') {
    return `https://tidal.com/search?q=${encodeURIComponent(song.title + ' ' + song.artist.name)}`;
  }

  return null;
}